import styles from './styles.module.scss';

import React from 'react';
import {Link} from 'react-router-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {Badge, PopoverBody} from 'reactstrap';
import {get, keys} from 'lodash/object';
import {isEmpty} from 'lodash/lang';
import NumberFormat from 'react-number-format';
import LabelStatus from './LabelStatus';
import LabelBadge from './LabelBadge';
import {useCommonData} from 'contexts/CommonDataContext';

import {
  ACCOUNT_ROLE_OPTIONS,
  ACCOUNT_STATUSES,
  COMPANY_STATUSES,
  CB_STANDARD_STATUSES,
  ENQUIRY_STATUSES,
  CERTIFICATION_STATUSES,
  WATCH_LIST_STATUSES,
  PARTICIPATION_STATUS,
  PARTICIPATION_STATUS_FILTER,
  CONFORMANCE_STATUS,
  CONFORMANCE_STATUS_FILTER,
} from 'configs';
import {ACCREDITATION_STATUS} from 'configs/statuses';

import {CommonDataConsumer} from 'contexts/CommonDataContext';
import {date as getDate} from 'utils/dates';
import {UncontrolledPopover} from 'reactstrap';
import {head, tail, values} from 'lodash';
import LabelParticipation from './LabelParticipation';
import LabelConformance from './LabelConformance';

export class LabelAccreditationStatus extends React.Component {
  render() {
    const {status, ...rest} = this.props;
    let color;

    switch (status) {
      default:
        color = 'muted';
        break;
      case ACCREDITATION_STATUS.active:
        color = 'success';
        break;
      case ACCREDITATION_STATUS.withdrawn:
        color = 'danger';
        break;
    }

    return <LabelStatus color={color} status={status} {...rest} />;
  }
}

export class LabelEnquiryStatus extends React.Component {
  render() {
    const {status, ...rest} = this.props;
    let color;

    switch (status) {
      default:
        color = 'muted';
        break;
      case ENQUIRY_STATUSES.open:
        color = 'success';
        break;
      case ENQUIRY_STATUSES.closed:
        color = 'muted';
        break;
    }

    return <LabelStatus color={color} status={status} {...rest} />;
  }
}

export class LabelAccountStatus extends React.Component {
  render() {
    const {status, ...rest} = this.props;
    let color;

    switch (status) {
      default: // pending, withdrawn, etc...
        color = 'muted';
        break;
      case ACCOUNT_STATUSES.active:
        color = 'success';
        break;
      case ACCOUNT_STATUSES.inactive:
        color = 'warning';
        break;
      case ACCOUNT_STATUSES.suspended:
        color = 'danger';
        break;
    }

    return <LabelStatus color={color} status={status} {...rest} />;
  }
}

export class LabelAccountRole extends React.Component {
  static defaultProps = {
    tag: 'span',
    textOnly: false,
    size: 'md',
    accountRole: 'staff',
  };

  static propTypes = {
    accountRole: PropTypes.oneOf(keys(ACCOUNT_ROLE_OPTIONS)).isRequired,
    textOnly: PropTypes.bool,
    size: PropTypes.oneOf(['sm', 'md', 'lg']),
  };

  render() {
    const {tag, accountRole, textOnly, size, className} = this.props;

    if (textOnly) {
      return ACCOUNT_ROLE_OPTIONS[accountRole] || 'Unknown Status';
    }

    return (
      <Badge
        tag={tag}
        className={classNames(styles['account-role'], {
          [styles[`account-role--${size}`]]: size,
          [styles[`account-role--${accountRole}`]]: accountRole,
          className,
        })}
      >
        {ACCOUNT_ROLE_OPTIONS[accountRole] || 'Unknown Status'}
      </Badge>
    );
  }
}

export class LabelCompanyStatus extends React.Component {
  static propTypes = {
    status: PropTypes.oneOf(keys(COMPANY_STATUSES)).isRequired,
  };

  render() {
    const {status, ...rest} = this.props;
    let color;

    switch (status) {
      default: // pending, withdrawn, etc...
        color = 'muted';
        break;
      case COMPANY_STATUSES.active:
        color = 'success';
        break;
      case COMPANY_STATUSES.inactive:
        color = 'warning';
        break;
      case COMPANY_STATUSES.suspended:
        color = 'danger';
        break;
    }

    return <LabelStatus color={color} status={status} {...rest} />;
  }
}

export class LabelStandardStatus extends React.Component {
  static propTypes = {
    // AB can see deleted status
    status: PropTypes.oneOf(keys(CB_STANDARD_STATUSES)).isRequired,
  };

  render() {
    const {status, ...rest} = this.props;
    let color;

    switch (status) {
      default: // pending, etc...
        color = 'muted';
        break;
      case CB_STANDARD_STATUSES.active:
        color = 'success';
        break;
      case CB_STANDARD_STATUSES.withdrawn:
        color = 'danger';
        break;
      case CB_STANDARD_STATUSES.suspended:
        color = 'warning';
        break;
      case CB_STANDARD_STATUSES.deleted:
        color = 'danger';
        break;
    }

    return <LabelStatus color={color} status={status} {...rest} />;
  }
}

export class LabelWatchListStatus extends React.Component {
  static propTypes = {
    status: PropTypes.oneOf(keys(WATCH_LIST_STATUSES)).isRequired,
  };

  render() {
    const {status, ...rest} = this.props;
    let color;

    switch (status) {
      default: // pending, withdrawn, etc...
        color = 'muted';
        break;
      case WATCH_LIST_STATUSES.active:
        color = 'success';
        break;
      case WATCH_LIST_STATUSES.inactive:
        color = 'warning';
        break;
    }

    return <LabelStatus color={color} status={status} {...rest} />;
  }
}

export class LabelCompanyName extends React.Component {
  static defaultProps = {
    tag: 'h1',
  };

  render() {
    const {tag, acronym, name, to, className} = this.props;

    const companyName = `${name}${acronym ? ` (${acronym})` : ''}`;

    return React.createElement(
      tag,
      {className: className},
      to ? <Link to={to}>{companyName}</Link> : companyName
    );
  }
}

export class LabelCertStatus extends React.Component {
  static propTypes = {
    status: PropTypes.oneOf(keys(CERTIFICATION_STATUSES)).isRequired,
  };

  render() {
    const {status, ...rest} = this.props;
    let color;

    switch (status) {
      default: // pending, withdrawn, etc...
        color = 'muted';
        break;
      case CERTIFICATION_STATUSES.active:
        color = 'success';
        break;
      case CERTIFICATION_STATUSES.inactive:
        color = 'warning';
        break;
      case CERTIFICATION_STATUSES.suspended:
        color = 'danger';
        break;
    }

    return <LabelStatus color={color} status={status} {...rest} />;
  }
}

export class LabelNumber extends React.Component {
  render() {
    return (
      <NumberFormat
        displayType="text"
        value={this.props.value || 0}
        thousandSeparator
      />
    );
  }
}

export class LabelCountry extends React.Component {
  static defaultProps = {
    tag: 'div',
  };

  render() {
    const {tag, className, country_id, children} = this.props;

    if (country_id) {
      return (
        <CommonDataConsumer>
          {({countries}) => {
            const country = countries[country_id];
            const name = get(country, 'name', '');

            return tag
              ? React.createElement(
                  tag,
                  {className: classNames(styles['label-country'], className)},
                  name
                )
              : name;
          }}
        </CommonDataConsumer>
      );
    }

    return React.createElement(
      tag,
      {className: classNames(styles['label-country'], className)},
      children
    );
  }
}

export class LabelAddress extends React.Component {
  render() {
    const {address, street, city, suburb, state, country, postcode} =
      this.props;

    const fields = [street, city, suburb, state, postcode, country]
      .filter((v) => v)
      .join(', ');

    // prioritize fields over full address if both existing
    // example of this scenario is when user uploaded a file with both full address and individual fields
    if (address && (!!city || !!street)) {
      return fields;
    }

    return address || fields;
  }
}

// This component is used for sites that joins the full address with country
export function LabelAddressSites(props) {
  const {address, street, city, suburb, state, country, postcode} = props;
  const {countries} = useCommonData();

  const fields = [street, city, suburb, state, postcode, country]
    .filter((v) => v)
    .join(', ');

  // prioritize fields over full address if both existing
  // example of this scenario is when user uploaded a file with both full address and individual fields
  if (address && (!!city || !!street)) {
    return fields;
  }

  if (!countries || typeof countries !== 'object') {
    const addressWithCountry =
      address && country && [address, country].filter(Boolean).join(', ');
    return addressWithCountry || fields;
  }

  const countryNames = Object.values(countries)
    .map((country) => country?.name)
    .filter((name) => name && typeof name === 'string');

  // Helper function to create variations of country names
  const getCountryVariations = (name) => {
    if (!name || typeof name !== 'string') {
      return [];
    }

    const variations = [name]; // Start with full name

    try {
      // Remove square brackets content: "Falkland Islands (the) [Malvinas]" -> "Falkland Islands (the)"
      let withoutBrackets = name.replace(/\s*\[.*?\]\s*/g, '').trim();
      if (withoutBrackets && withoutBrackets !== name) {
        variations.push(withoutBrackets);
      }

      // Remove parentheses content progressively
      let current = withoutBrackets;
      let iterations = 0; // Prevent infinite loops
      while (current && current.includes('(') && iterations < 5) {
        // Remove the last parentheses: "Cocos (Keeling) Islands (the)" -> "Cocos (Keeling) Islands"
        current = current.replace(/\s*\([^)]*\)\s*$/, '').trim();
        if (current) {
          variations.push(current);
        }

        // Also try removing all parentheses content
        let withoutAllParens = current.replace(/\s*\([^)]*\)\s*/g, '').trim();
        if (withoutAllParens && withoutAllParens !== current) {
          variations.push(withoutAllParens);
        }

        iterations++;
      }

      // Filter out empty strings and duplicates
      const filteredVariations = variations.filter((v) => v && v.trim());
      return Array.from(new Set(filteredVariations));
    } catch (error) {
      // If anything goes wrong, return just the original name
      return [name];
    }
  };

  // Check if address ends with a country name and replace it with the country variable
  if (address && country) {
    let modifiedAddress = address;

    // Find if the address ends with any country name from countryNames
    const matchingCountry = countryNames.find((countryName) => {
      const addressTrimmed = address.trim();

      // Test all variations of the country name
      const variations = getCountryVariations(countryName);

      for (const variation of variations) {
        if (variation && variation.trim()) {
          try {
            const escapedVariation = variation.replace(
              /[.*+?^${}()|[\]\\]/g,
              '\\$&'
            );
            const pattern = new RegExp(`\\b${escapedVariation}\\s*$`, 'i');
            if (pattern.test(addressTrimmed)) {
              return true;
            }
          } catch (error) {
            // Skip this variation if regex creation fails
            continue;
          }
        }
      }

      return false;
    });

    if (matchingCountry) {
      // Determine which version of the country name to remove from the address
      const addressTrimmed = address.trim();
      let countryNameToRemove = null;

      // Find which variation actually matches the address
      const variations = getCountryVariations(matchingCountry);

      for (const variation of variations) {
        if (variation && variation.trim()) {
          try {
            const escapedVariation = variation.replace(
              /[.*+?^${}()|[\]\\]/g,
              '\\$&'
            );
            const pattern = new RegExp(`\\b${escapedVariation}\\s*$`, 'i');
            if (pattern.test(addressTrimmed)) {
              countryNameToRemove = variation;
              break;
            }
          } catch (error) {
            // Skip this variation if regex creation fails
            continue;
          }
        }
      }

      if (countryNameToRemove) {
        try {
          // Remove the country name from the end of the address
          const escapedCountryName = countryNameToRemove.replace(
            /[.*+?^${}()|[\]\\]/g,
            '\\$&'
          );
          const pattern = new RegExp(`\\b${escapedCountryName}\\s*$`, 'i');
          modifiedAddress = address.replace(pattern, '').trim();

          // Remove trailing comma if present
          modifiedAddress = modifiedAddress.replace(/,\s*$/, '');
        } catch (error) {
          // If regex fails, just use the original address
          modifiedAddress = address;
        }
      }
    }

    return [modifiedAddress, country].filter(Boolean).join(', ');
  }

  return fields;
}

export class LabelDate extends React.Component {
  static defaultProps = {
    format: 'YYYY-MM-DD',
  };

  render() {
    const {date, timestamp, format} = this.props;

    if (!isEmpty(date)) return getDate(date, format);

    return timestamp ? getDate(timestamp, format) : '-';
  }
}

export class ItemsAndMoreLabel extends React.Component {
  constructor(props) {
    super(props);
    this.labelRef = React.createRef();
  }

  render() {
    const {items, renderItem} = this.props;

    const firstItem = head(items);
    const restItems = tail(items);

    return (
      <>
        {renderItem(firstItem)}
        {restItems.length > 0 && (
          <span
            ref={this.labelRef}
            className="ml-1 text-primary"
            style={{cursor: 'pointer'}}
          >
            + {restItems.length} more
          </span>
        )}
        <UncontrolledPopover
          placement="bottom"
          target={this.labelRef}
          trigger="legacy"
          hideArrow
        >
          <PopoverBody>
            {restItems.map((item, index) => (
              <label key={index} className="font-weight-normal d-block">
                {renderItem(item)}
              </label>
            ))}
          </PopoverBody>
        </UncontrolledPopover>
      </>
    );
  }
}

export class LabelCertStatusBadge extends React.Component {
  static propTypes = {
    status: PropTypes.oneOf(keys(CERTIFICATION_STATUSES)).isRequired,
  };

  render() {
    const {status, ...rest} = this.props;
    let color;

    switch (status) {
      default: // pending, withdrawn, etc...
        color = 'secondary';
        break;
      case CERTIFICATION_STATUSES.active:
        color = 'success';
        break;
      case CERTIFICATION_STATUSES.inactive:
        color = 'warning';
        break;
      case CERTIFICATION_STATUSES.suspended:
        color = 'danger';
        break;
    }

    return <LabelBadge color={color} status={status} {...rest} />;
  }
}

export class LabelParticipationBadge extends React.Component {
  static propTypes = {
    status: PropTypes.oneOf(values(PARTICIPATION_STATUS)),
  };
  //change keys for Participation Status
  render() {
    const {status, ...rest} = this.props;
    let color;
    let _status;

    switch (status) {
      case PARTICIPATION_STATUS['participating']:
      case PARTICIPATION_STATUS_FILTER['P']:
        color = 'success';
        _status = PARTICIPATION_STATUS['participating'];
        break;
      case PARTICIPATION_STATUS['non participating']:
      case PARTICIPATION_STATUS_FILTER['NP']:
        color = 'danger';
        _status = PARTICIPATION_STATUS['non participating'];
        break;
      default:
        color = 'secondary';
        _status = 'Unknown Status';
        break;
    }

    return <LabelParticipation color={color} status={_status} {...rest} />;
  }
}
export class LabelConformanceBadge extends React.Component {
  static propTypes = {
    status: PropTypes.oneOf(values(CONFORMANCE_STATUS)),
  };
  //change keys for Conformance Status
  render() {
    const {status, ...rest} = this.props;
    let color;
    let _status;

    switch (status) {
      default:
        color = 'secondary';
        _status = 'Unknown Status';
        break;
      case CONFORMANCE_STATUS['conformance']:
      case CONFORMANCE_STATUS_FILTER['C']:
        color = 'success';
        _status = CONFORMANCE_STATUS['conformance'];
        break;
      case CONFORMANCE_STATUS['non conformance']:
      case CONFORMANCE_STATUS_FILTER['NC']:
        color = 'danger';
        _status = CONFORMANCE_STATUS['non conformance'];
        break;
      case CONFORMANCE_STATUS['exclusion conformance']:
        color = 'success';
        _status = CONFORMANCE_STATUS['exclusion conformance'];
        break;
    }

    return <LabelConformance color={color} status={_status} {...rest} />;
  }
}

export {default as LabelRequired} from './LabelRequired';
